<script setup>
// Wydarzenie w kalendarzu: nazwa, początek i koniec (albo cały dzień), miejsce i opis.
// Przy włączonej synchronizacji zapis od razu trafia do Google Calendar.
import { computed, onMounted, watch } from 'vue';
import DaySelect from '@/components/v2/add/day-select.vue';
import TimeInput from '@/components/v2/time-input.vue';
import GoogleG from '@/components/v2/google-g.vue';
import { flattenPasted } from '@/composables/useMapsLocation.js';
import SelectField from '@/components/v2/select-field.vue';
import { eventLocationText, useMiejsca } from '@/composables/useMiejsca.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    draft: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    // Edytowane wydarzenie (null przy dodawaniu) — mówi, czy jest w Google i czy należy do serii.
    entry: { type: Object, default: null },
});

const { t } = useI18n();

// DaySelect zmienia `draft.day`; dla końca podajemy mu obiekt, którego `day` to `endDay`.
const endProxy = {
    get day() { return props.draft.endDay; },
    set day(value) { props.draft.endDay = value; },
};

// Przesunięcie początku przesuwa koniec o tyle samo — czas trwania zostaje.
// Podmiana całego szkicu (reset modalu) to nie przesunięcie, więc ją pomijamy.
watch(() => [props.draft, props.draft.day], ([draft, next], [prevDraft, prev]) => {
    if (draft !== prevDraft || !prev || !next || !draft.endDay) return;
    const delta = new Date(next).getTime() - new Date(prev).getTime();
    draft.endDay = new Date(new Date(draft.endDay).getTime() + delta);
});

const minutes = (hhmm) => {
    const [h, m] = String(hhmm ?? '').split(':').map(Number);
    return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};
const pad = (n) => String(n).padStart(2, '0');

// „Udostępnij” w Google Maps daje nazwę i link w dwóch liniach; wklejamy je jako
// jeden wiersz w miejscu kursora.
const pasteLocation = (event) => {
    const text = event.clipboardData?.getData('text');
    if (!text || !/[\r\n]/.test(text)) return;
    event.preventDefault();
    const input = event.target;
    const value = input.value ?? '';
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    props.draft.location = value.slice(0, start) + flattenPasted(text) + value.slice(end);
};

// Zapisane Miejsca: wybrane wstawia się jako tekst (nazwa i link z punktem) — kopia,
// wydarzenie nie zmienia się razem z miejscem. Przypięte są pierwsze.
const { places, loadPlaces } = useMiejsca();
onMounted(() => { if (!places.value.length) loadPlaces().catch(() => { /* bez podpowiedzi */ }); });
const placeOptions = computed(() => {
    const option = (p) => ({ value: p.uuid, label: p.address ? `${p.name} · ${p.address}` : p.name });
    const pinned = places.value.filter(p => p.pinned);
    const rest = places.value.filter(p => !p.pinned).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    return [
        ...(pinned.length ? [{ label: t('entry.event.pinnedPlaces'), items: pinned.map(option) }] : []),
        ...(rest.length ? [{ label: pinned.length ? t('entry.event.otherPlaces') : t('entry.event.myPlaces'), items: rest.map(option) }] : []),
    ];
});
const pickPlace = (uuid) => {
    const place = places.value.find(p => p.uuid === uuid);
    if (place) props.draft.location = eventLocationText(place);
};

watch(() => [props.draft, props.draft.time],([draft, next], [prevDraft, prev]) => {
    if (draft !== prevDraft) return;
    const from = minutes(prev);
    const to = minutes(next);
    const end = minutes(draft.endTime);
    if (from == null || to == null || end == null) return;
    const shifted = ((end + to - from) % 1440 + 1440) % 1440;
    draft.endTime = `${pad(Math.floor(shifted / 60))}:${pad(shifted % 60)}`;
});
</script>

<template>
    <div>
        <input
            v-model="draft.title"
            type="text"
            class="v2-freetext mb-1"
            :placeholder="t('entry.event.titlePlaceholder')"
            :aria-invalid="!!errors.title"
        />
        <p v-if="errors.title" class="text-[11.5px] text-[#B5643C] mb-2">{{ errors.title }}</p>

        <div class="border-t border-[#EFE9DF] pt-4 mt-4 mb-4">
            <label class="flex items-center gap-2.5 mb-3 cursor-pointer w-fit">
                <input v-model="draft.allDay" type="checkbox" class="w-[15px] h-[15px] accent-[#3F8A8C]" />
                <span class="text-[13px] text-[#4A443C]">{{ t('entry.event.allDay') }}</span>
            </label>

            <div class="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-3">
                <DaySelect :draft="draft" :label="draft.allDay ? t('entry.event.firstDay') : t('entry.event.start')" input-id="v2-event-day" />
                <div v-if="!draft.allDay" class="sm:w-[168px]">
                    <label class="v2-label" for="v2-time">{{ t('entry.fields.time') }}</label>
                    <TimeInput id="v2-time" v-model="draft.time" :invalid="!!errors.time" />
                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <DaySelect :draft="endProxy" :label="draft.allDay ? t('entry.event.lastDay') : t('entry.event.end')" input-id="v2-event-end-day" />
                <div v-if="!draft.allDay" class="sm:w-[168px]">
                    <label class="v2-label" for="v2-event-end">{{ t('entry.fields.time') }}</label>
                    <TimeInput id="v2-event-end" v-model="draft.endTime" :invalid="!!errors.endTime" />
                </div>
            </div>
            <p v-if="errors.time || errors.endTime || errors.endDay" class="text-[11.5px] text-[#B5643C] mt-1">
                {{ errors.time || errors.endTime || errors.endDay }}
            </p>
        </div>

        <div class="mb-4">
            <label class="v2-label" for="v2-event-location">{{ t('entry.event.location') }}</label>
            <input
                id="v2-event-location"
                v-model="draft.location"
                type="text"
                class="v2-input"
                :placeholder="t('entry.event.locationPlaceholder')"
                @paste="pasteLocation"
                :aria-invalid="!!errors.location"
            />
            <p v-if="errors.location" class="text-[11.5px] text-[#B5643C] mt-1">{{ errors.location }}</p>
            <div v-if="places.length" class="mt-2">
                <SelectField
                    id="v2-event-place"
                    :model-value="null"
                    :options="placeOptions"
                    :placeholder="t('entry.event.pickPlace')"
                    :search-placeholder="t('entry.event.searchPlace')"
                    @update:model-value="pickPlace"
                />
            </div>
        </div>

        <div>
            <label class="v2-label" for="v2-event-description">{{ t('entry.event.description') }}</label>
            <textarea
                id="v2-event-description"
                v-model="draft.content"
                rows="3"
                class="v2-input v2-textarea"
                :placeholder="t('entry.event.descriptionPlaceholder')"
            ></textarea>
        </div>

        <p v-if="entry?.recurring" class="text-[11.5px] text-[#8A8378] mt-3">
            <i class="pi pi-replay text-[10px] mr-1" aria-hidden="true"></i>
            {{ t('entry.event.recurringInfo') }}
        </p>
        <p v-else-if="entry?.linked" class="flex items-center gap-1.5 text-[11.5px] text-[#8A8378] mt-3">
            <GoogleG :size="11" :muted="!!entry.sync_pending" />
            {{ entry.sync_pending ? t('entry.event.linkedPending') : t('entry.event.linked') }}
        </p>
    </div>
</template>
